import { CoreFactories } from '../../bff';
import { getBrandAndBrandOwner, isSelectedBrandOrBrandOwnerInList } from '../../common/brandOwner.utils';
import { Formatting } from '../../data/formatting';
import { DataStore } from '../../data/store';
import { ColumnRelationship, ColumnType, DataColumn, DataRow, KpiWithChangeColumn, ListBoxMenuItem } from '../../types';
import {
  IndicatorColumnConfig,
  IndicatorColumnInfo,
  IndicatorConfig,
  IndicatorInfo,
  IndicatorRow,
  IndicatorSparkInfo,
} from './types';
import { BRAND_KEY, BRAND_OWNER_DELIMITER, BRAND_OWNER_KEY } from '../../common/brandOwner.constants';

export function makeIndicatorInfo(
  data: DataStore,
  options: {
    configs: IndicatorConfig[];
    grouping: string;
    fixedItems?: string[];
    limitedItems?: string[];
  }
): { rows: IndicatorRow[]; rowItems: ListBoxMenuItem[]; filteredLimitedItems: string[] } {
  const { configs, grouping, fixedItems, limitedItems } = options;
  const rowNames = new Set<string>();
  const rowIdentifiers: ListBoxMenuItem[] = [];
  const dataRows: DataRow[] = [];
  const groupedData = data.getGroupedData([grouping]);
  const filteredLimitedItems: string[] = [];

  const candidateRows = groupedData.flatMap(g => g.data);
  const addEverything = !fixedItems?.length && !limitedItems?.length;

  candidateRows.forEach(row => {
    const isFixed = isSelectedBrandOrBrandOwnerInList(row, grouping, fixedItems);
    const isLimited = isSelectedBrandOrBrandOwnerInList(row, grouping, limitedItems);
    if (addEverything || isFixed || isLimited) {
      const allSelectedItems = [...fixedItems, ...limitedItems];

      const actualItems = allSelectedItems
        .filter(item => item.includes(BRAND_OWNER_DELIMITER))
        .find(item => {
          const { brand, brandOwner } = getBrandAndBrandOwner(item);

          return brand ? brand === row.values[BRAND_KEY] : brandOwner === row.values[BRAND_OWNER_KEY];
        });

      if (actualItems) {
        filteredLimitedItems.push(actualItems);
      }

      dataRows.push(row);
    }

    const [value, label] = getRowIdentifiers(row, data.getDataColumns(), grouping);
    if (!rowNames.has(value)) {
      rowNames.add(value);
      rowIdentifiers.push({
        type: 'MenuItem',
        value,
        label,
      });
    }
  });

  const unSortedRows: IndicatorRow[] = [];

  dataRows.forEach(row => {
    const [value, label] = getRowIdentifiers(row, data.getDataColumns(), grouping);

    const indicatorRow: IndicatorRow = {
      value: value ? value : undefined,
      label,
      indicators: [],
      isFixed: addEverything || fixedItems?.includes(value),
    };

    configs.forEach(config => {
      const { column, secondaryColumn, hideChange = false, includeSpark } = config;

      const info: IndicatorInfo = {
        mainColumn: makeColumnInfo(row, data, column, hideChange),
        secondaryColumn: secondaryColumn && makeColumnInfo(row, data, secondaryColumn, hideChange),
        sparkLine: includeSpark && makeSparkInfo(row, data, column),
        clickAction: config.clickAction,
      };

      indicatorRow.indicators.push(info);
    });

    unSortedRows.push(indicatorRow);
  });

  const sortedRows: IndicatorRow[] = addEverything ? unSortedRows : [];
  if (!addEverything) {
    addToSortedRows(fixedItems, unSortedRows, sortedRows, true);
    addToSortedRows(limitedItems, unSortedRows, sortedRows, false);
  }

  rowIdentifiers.sort((a, b) => a.label.localeCompare(b.label));
  return {
    rows: sortedRows,
    rowItems: CoreFactories.selectionMenu.items(rowIdentifiers),
    filteredLimitedItems,
  };
}

export function addToSortedRows(
  items: string[],
  unSortedRows: IndicatorRow[],
  sortedRows: IndicatorRow[],
  isFixed: boolean
) {
  items?.forEach(item => {
    const { brand, brandOwner } = getBrandAndBrandOwner(item);
    //if brand is null, then we search for brandOwner
    const searchValue = brand ?? brandOwner;
    const row = unSortedRows.find(row => row.value === searchValue);
    if (row && !sortedRows.some(r => r.value === row.value)) {
      row.isFixed = isFixed;
      sortedRows.push(row);
    }
  });
}

function getRowIdentifiers(row: DataRow, columns: DataColumn[], grouping: string): [value: string, name: string] {
  let rowValue: string;
  let rowLabel: string;

  const identifiers = DataStore.getRowIdentifiers(row, columns, grouping);
  if (identifiers) {
    rowValue = identifiers.id;
    rowLabel = identifiers.name;
  } else {
    const groupingColumn = columns.find(col => col.name === grouping);
    if (groupingColumn) {
      rowValue = String(row.values[groupingColumn.name]);
      const descColumn = columns.find(
        col => col.relatedTo === groupingColumn.name && col.relationship === ColumnRelationship.Description
      );
      rowLabel = descColumn ? String(row.values[descColumn.name]) : 'nope';
    }
  }

  return [rowValue, rowLabel];
}

function makeSparkInfo(row: DataRow, data: DataStore, config: IndicatorColumnConfig): IndicatorSparkInfo | undefined {
  const { name } = config;
  const column = data.getDataColumns().find(col => col.name === name);
  const currency = data.getCurrency();

  if (!column) {
    return;
  }

  const values = row.values[column.name];
  if (!Array.isArray(values)) {
    return;
  }

  let isNegative = values[values.length - 1] < values[0];
  const startValue = Formatting.format(values[0], {
    dataType: column.dataType,
    formatHint: column.formatHint,
    currency,
  });
  const endValue = Formatting.format(values[values.length - 1], {
    dataType: column.dataType,
    formatHint: column.formatHint,
    currency,
  });

  return {
    values,
    isNegative,
    startValue,
    endValue,
  };
}

function makeColumnInfo(
  row: DataRow,
  data: DataStore,
  config: IndicatorColumnConfig,
  hideChange: boolean
): IndicatorColumnInfo {
  const { name, label } = config;
  const column = data.getDataColumns().find(col => col.name === name);
  const currency = data.getCurrency();

  if (!column) {
    return {
      label: label || `${name} NOT FOUND`,
      value: `??`,
      name: name,
    };
  }

  const rawValue = DataStore.getCellsLatestValue(row, column.name);
  let textValue = '-';
  let columnLabel = label || column.label;

  if (rawValue === null) {
    return {
      label: columnLabel,
      value: textValue,
      name: column.name,
    };
  }

  textValue = Formatting.format(rawValue, {
    dataType: column.dataType,
    formatHint: column.formatHint,
    currency,
  });

  const info: IndicatorColumnInfo = {
    label: columnLabel,
    value: textValue,
    name: column.name,
  };

  if (!hideChange && column.type === ColumnType.KpiWithChange) {
    const withChangeColumn = column as KpiWithChangeColumn;
    const rawChangeValue = DataStore.getCellsLatestValue(row, withChangeColumn.changeName);
    const textChangeValue = Formatting.format(rawChangeValue, {
      dataType: withChangeColumn.changeDataType,
      formatHint: withChangeColumn.changeFormatHint,
      isChange: true,
      currency,
    });

    info.changeRaw = rawChangeValue;
    info.changeValue = textChangeValue;
  }

  return info;
}
